import { useState, useCallback, useMemo, useRef } from 'react'

export interface DrawerSlot<T> {
  isOpen: boolean
  data: T | undefined
  open: (value: T) => void
  close: () => void
  afterClose: () => void
  reset: () => void
}

export function useDrawerSlot<T>(): DrawerSlot<T> {
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<T | undefined>(undefined)
  const isOpenRef = useRef(false)

  const open = useCallback((value: T) => {
    setData(value)
    setIsOpen(true)
    isOpenRef.current = true
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    isOpenRef.current = false
  }, [])

  const afterClose = useCallback(() => {
    // Guard: if a new drawer opened before this animation callback fired,
    // don't clear the replacement drawer's data.
    if (!isOpenRef.current) {
      setData(undefined)
    }
  }, [])

  const reset = useCallback(() => {
    setIsOpen(false)
    setData(undefined)
    isOpenRef.current = false
  }, [])

  return useMemo(
    () => ({ isOpen, data, open, close, afterClose, reset }),
    [isOpen, data, open, close, afterClose, reset],
  )
}
